import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import styles from "./multi-verse-slide.css";

import type { SlideState, VerseData } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";
import type { TemplateResult } from "lit";

const ANY_ADAPTED_STRING = "*Key Words Adapted by Teacher";

@customElement("bp-multi-verse-slide")
export class MultiVerseSlide extends LitElement {
  static styles = styles;

  @property()
  multiVerseData: Array<VerseData> = [];

  @property()
  slideState?: SlideState;

  @property({ attribute: "title" })
  slideTitle?: string;

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  getAnyAdapted = (): TemplateResult => {
    const anyAdapted = this.multiVerseData
      .map((verseData) => verseData.isAdapted)
      .reduce((a, b) => a || b);

    return anyAdapted
      ? html`<div class="caption">${ANY_ADAPTED_STRING}</div>`
      : html``;
  };

  getMultiVerseContent = (): TemplateResult => {
    const subtitleIds = new Set<string>([]);
    const verseContent = [];
    for (const verseData of this.multiVerseData) {
      let verseSubtitleId = "";
      let verseSubtitle = html``;
      let verseText = html``;

      if (verseData.subtitle) {
        verseSubtitleId = verseData.subtitle
          .split(" ")
          ?.map((value) => value.toLowerCase())
          .slice(0, 2)
          .join("-");

        if (subtitleIds.has(verseSubtitleId)) {
          // Handle duplicate subtitle ids
          const baseSubtitle = verseSubtitleId;
          let subtitleDupeCount = 2;
          while (subtitleIds.has(verseSubtitleId)) {
            verseSubtitleId = `${baseSubtitle}-${subtitleDupeCount}`;
            subtitleDupeCount += 1;
          }
        }
        subtitleIds.add(verseSubtitleId);
        verseSubtitle = html`<div
          class="verse-subtitle"
          data-id="${verseSubtitleId}"
        >
          ${verseData.subtitle}
        </div>`;
      }

      if (verseData.reference && verseData.translation && verseData.text) {
        verseText = html`<div
          data-id="${verseData.reference} (${verseData.translation})"
        >
          <div class="verse-header">
            ${verseData.reference}
            <sup class="translation">
              ${verseData.translation + (verseData.isAdapted ? "*" : "")}
            </sup>
          </div>
          <div class="verse-content">${unsafeHTML(verseData.text)}</div>
        </div>`;
      }
      verseContent.push(html` ${verseSubtitle} ${verseText} `);
    }

    return html`${verseContent}`;
  };

  render() {
    if (!this.multiVerseData?.length) return null;

    return html`
      <bp-scrollable-slide
        view-mode="${this.viewMode}"
        .slideState=${this.slideState}
      >
        <div slot="title">${unsafeHTML(this.slideTitle)}</div>
        <div slot="content" class="multi-verse-container">
          ${this.getMultiVerseContent()} ${this.getAnyAdapted()}
        </div>
      </bp-scrollable-slide>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-multi-verse-slide": MultiVerseSlide;
  }
}
